import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertDocumentOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertDocumentOversightResultInput = {
  OversightAssessmentId: string;
  DocumentIds: string[];
  // COI-650: V2 required Rating on this insert; the form schema has always
  // made it mandatory, so the type now matches.
  Rating: number;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertDocumentOversightResultMutation => ({
  insertChildDocumentOversightResult: {
    __typename: 'InsertChildDocumentAssessmentResultOutput',
    Ids: trpcData.Ids,
  },
});

export const useInsertDocumentOversightResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.insertDocumentOversightResult.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          // Apollo parity: evictField(cache, 'document_oversight_result')
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.assessment.oversightResultById.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.documentOversightResultById.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightAssessmentDocumentAssessmentResultsByDocumentId.pathKey(),
          }),
          // Apollo parity: evictField(cache, 'document_oversight_result_aggregate')
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
          }),
          // Apollo parity: evictField(cache, 'oversight_assessment')
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightActivityRegister.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightActivityCount.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertDocumentOversightResult: async (
      variables: InsertDocumentOversightResultInput
    ): Promise<InsertDocumentOversightResultMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
